import React, { useState, useEffect } from "react";
import { useGameStateContext } from "../GameStateProvider";
import { useModalActions } from "../hooks/useModalActions";
import { CONSTANTS } from "../data";
import DailyBlendBanner from "./DailyBlendBanner";
import HeaderStats from "./header/HeaderStats";
import GameModeDisplay from "./header/GameModeDisplay";
import HeaderEnvironmentalDisplay from "./header/HeaderEnvironmentalDisplay";
import StatusIcons from "./header/StatusIcons";
import HeaderActionButtons from "./header/HeaderActionButtons";
import MobileMenu from "./header/MobileMenu";

const HeaderBar: React.FC = () => {
  const { state, dispatch } = useGameStateContext();
  const { handleShowShop, handleShowChallenges, handleShowRecipeBook, handleShowSettings } = useModalActions();
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  const {
    coins,
    xp,
    level,
    prestigeLevel,
    reputation,
    environmentalState,
    dailyBlend,
    seededSession,
    activeSeason,
    legendaryUnlocks,
    sessionMode,
    aiBarista,
  } = state;

  const [rushTimeRemaining, setRushTimeRemaining] = useState<number | null>(null);

  const handleToggleAI = () => dispatch({ type: 'TOGGLE_AI_BARISTA' });

  const handleTooltipToggle = (tooltipId: string) => {
    setOpenTooltip(prev => (prev === tooltipId ? null : tooltipId));
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(prev => !prev);
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openTooltip && !(event.target as HTMLElement).closest('.tooltip-trigger')) {
        setOpenTooltip(null);
      }
      if (mobileMenuOpen && !(event.target as HTMLElement).closest('.mobile-menu-container')) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openTooltip, mobileMenuOpen]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (sessionMode.type === 'rush') {
      const calculateRemaining = () => {
        const elapsed = Date.now() - sessionMode.startTime;
        const remaining = Math.max(0, CONSTANTS.RUSH_MODE.duration - elapsed);
        setRushTimeRemaining(Math.ceil(remaining / 1000));
      };

      calculateRemaining();
      interval = setInterval(calculateRemaining, 1000);
    } else {
      setRushTimeRemaining(null);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [sessionMode.type, sessionMode.startTime]);

  return (
    <header
      data-tutorial-id="stats-bar"
      className="header-bar bg-text-primary text-white shadow-md"
    >
      <div className="p-1.5 md:p-3">
        {/* Mobile Layout */}
        <div className="block sm:hidden">
          <div className="flex justify-between items-center">
            <div className="flex gap-1.5 items-center">
              <HeaderStats
                coins={coins}
                xp={xp}
                level={level}
                reputation={reputation}
                prestigeLevel={prestigeLevel}
                openTooltip={openTooltip}
                onToggleTooltip={handleTooltipToggle}
                isMobile={true}
              />
              <GameModeDisplay sessionMode={sessionMode} rushTimeRemaining={rushTimeRemaining} isMobile={true} />
              <HeaderEnvironmentalDisplay environmentalState={environmentalState} openTooltip={openTooltip} handleTooltipToggle={handleTooltipToggle} />
              <StatusIcons dailyBlend={dailyBlend} seededSession={seededSession} activeSeason={activeSeason} legendaryUnlocks={legendaryUnlocks} isMobile={true} />
            </div>
            <MobileMenu
              isOpen={mobileMenuOpen}
              onToggle={handleMobileMenuToggle}
              onShowShop={handleShowShop}
              onShowChallenges={handleShowChallenges}
              onShowRecipeBook={handleShowRecipeBook}
              onToggleAI={handleToggleAI}
              onShowSettings={handleShowSettings}
              aiBarista={aiBarista}
            />
          </div>
        </div>

        {/* Desktop Layout - removed overflow-x-auto and adjusted layout */}
        <div className="hidden sm:flex justify-between items-center gap-y-2 flex-wrap">
          <div className="flex gap-3 items-center flex-wrap">
            <HeaderStats
              coins={coins}
              xp={xp}
              level={level}
              reputation={reputation}
              prestigeLevel={prestigeLevel}
              openTooltip={openTooltip}
              onToggleTooltip={handleTooltipToggle}
              isMobile={false}
            />
            <GameModeDisplay sessionMode={sessionMode} rushTimeRemaining={rushTimeRemaining} isMobile={false} />
            <HeaderEnvironmentalDisplay environmentalState={environmentalState} openTooltip={openTooltip} handleTooltipToggle={handleTooltipToggle} />
            <StatusIcons dailyBlend={dailyBlend} seededSession={seededSession} activeSeason={activeSeason} legendaryUnlocks={legendaryUnlocks} isMobile={false} />
          </div>
          <HeaderActionButtons
            onShowShop={handleShowShop}
            onShowChallenges={handleShowChallenges}
            onShowRecipeBook={handleShowRecipeBook}
            onShowSettings={handleShowSettings}
            onToggleAI={handleToggleAI}
            aiBarista={aiBarista}
          />
        </div>
      </div>
      <DailyBlendBanner
        dailyBlend={dailyBlend}
        seededSession={seededSession}
      />
    </header>
  );
};

export default HeaderBar;
